// The three stages of one game as an object: where the opening ends and the
// endgame begins, decided once when the game opens, and the questions every
// tab, puzzle and history row asks of them.
//
// The stages split the game's moves into exclusive, contiguous spans: every
// move belongs to exactly one stage, and the next stage starts on the move
// after the previous one ends. If the opening ends at move 5, the middlegame
// starts at move 6. A stage ends on the move where its score is read: the
// reader's first move after the stage's raw boundary (the move that left the
// book for the opening, the position where the material says endgame for the
// middlegame), so the move a stage is scored on is part of that stage.
import { type Color } from '@chess/api-model';
import { type Phase, type ReviewPhases } from '@chess/api-model';

/** The side that played the move at a 1-based ply. */
function moverOf(ply: number): Color {
  return (ply - 1) % 2 === 0 ? 'white' : 'black';
}

/** The reader's first move after `ply`, or `ply` itself when none follows. */
function readerMoveAfter(ply: number, moveCount: number, reader: Color): number {
  for (let next = ply + 1; next <= moveCount; next++) {
    if (moverOf(next) === reader) return next;
  }
  return ply;
}

export class Stages {
  /** The opening's last move (1-based ply); its moves are `1 … openingEnd`. */
  readonly openingEnd: number;
  /** The middlegame's last move; its moves are `openingEnd + 1 … middlegameEnd`. The ending's are the rest. */
  readonly middlegameEnd: number;
  /** The first position the material calls an endgame, as decided when the game opened; undefined when never reached. */
  readonly endgameStart?: number;
  readonly moveCount: number;
  /** The side the spans are scored for; White when the reader is unknown. */
  readonly reader: Color;
  /** Who played the move out of the book; undefined when the game never left it or no book read it. */
  readonly leftBy?: Color;
  /** Index of the last position the book knows; undefined when no book read the game. */
  readonly lastKnownPly?: number;

  constructor(phases: ReviewPhases, moveCount: number, reader: Color = 'white') {
    this.moveCount = moveCount;
    this.reader = reader;
    this.endgameStart = phases.endgameStart;
    this.leftBy = phases.leftBy;
    this.lastKnownPly = phases.lastKnownPly;
    // The opening: up to the move that left the book, then on to the reader's
    // move where the opening is scored; the whole game when it never left.
    const bookEnd = Math.min(phases.openingEnd, moveCount);
    this.openingEnd = bookEnd >= moveCount ? moveCount : readerMoveAfter(bookEnd, moveCount, reader);
    // The middlegame: up to where the material says endgame, then on to the
    // reader's move where it is scored; never before the opening's end.
    if (phases.endgameStart === undefined || phases.endgameStart >= moveCount) {
      this.middlegameEnd = moveCount;
    } else if (phases.endgameStart <= this.openingEnd) {
      // The material said endgame before the opening was over: no middlegame.
      this.middlegameEnd = this.openingEnd;
    } else {
      this.middlegameEnd = readerMoveAfter(phases.endgameStart, moveCount, reader);
    }
  }

  /**
   * The positions a stage spans: `fromPly` the position it starts from (the
   * previous stage's last position, 0 for the opening) and `toPly` its last.
   * Its moves are `fromPly + 1 … toPly`, never shared with another stage.
   */
  range(phase: Phase): { fromPly: number; toPly: number } {
    switch (phase) {
      case 'opening':
        return { fromPly: 0, toPly: this.openingEnd };
      case 'middlegame':
        return { fromPly: this.openingEnd, toPly: this.middlegameEnd };
      case 'ending':
        return { fromPly: this.middlegameEnd, toPly: this.moveCount };
    }
  }

  /** The stage's moves as 1-based plies: first and last; undefined when the stage has none. */
  moves(phase: Phase): { first: number; last: number } | undefined {
    const { fromPly, toPly } = this.range(phase);
    return toPly > fromPly ? { first: fromPly + 1, last: toPly } : undefined;
  }

  /** The stage the move at a 1-based ply belongs to. */
  ofMove(ply: number): Phase {
    if (ply <= this.openingEnd) return 'opening';
    if (ply <= this.middlegameEnd) return 'middlegame';
    return 'ending';
  }

  /** The stage of the position after `ply` moves: the stage of the move that reached it; the start position is the opening's. */
  ofPosition(ply: number): Phase {
    return ply <= 0 ? 'opening' : this.ofMove(ply);
  }

  /**
   * Where a jump to a stage lands: the first position of the game that
   * belongs to it, so the jump never pulls a stage tab elsewhere. The
   * opening's is the start; another stage's is the position after its first
   * move; a stage the game ended before lands on the final position.
   */
  entryPly(phase: Phase): number {
    if (phase === 'opening') return 0;
    return this.moves(phase)?.first ?? this.moveCount;
  }

  /** The game ended before the stage began. */
  ended(phase: Phase): boolean {
    return phase === 'opening' ? this.moveCount === 0 : this.moves(phase) === undefined;
  }
}
